import { useRef, useState } from 'react';


export const CONVENTION_FILE_MAX_BYTES = 1024 * 1024;

interface ConventionEntry {
  file: File;
  convention_type: string;
  applies_to: string;
}

function classify(name: string): { type: string; appliesTo: string } {
  const n = name.toLowerCase();
  if (n.includes('eslint')) return { type: 'eslint', appliesTo: 'Coder' };
  if (n.includes('prettier')) return { type: 'prettier', appliesTo: 'Coder' };
  if (n.includes('stylelint')) return { type: 'stylelint', appliesTo: 'Reviewer' };
  if (n.includes('markdownlint')) return { type: 'markdownlint', appliesTo: 'Documenter' };
  if (n.includes('style-guide') || n.endsWith('.md')) return { type: 'style guide', appliesTo: 'Documenter' };
  return { type: 'project-specific other', appliesTo: 'Coder' };
}

export function ConventionUploadHelper({
  onChange
}: {
  onChange?: (files: File[]) => void;
}): JSX.Element {
  const [entries, setEntries] = useState<ConventionEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  function addFiles(files: FileList | null): void {
    if (!files || files.length === 0) return;
    setError(null);
    const next: ConventionEntry[] = [];
    for (const file of Array.from(files)) {
      if (file.size > CONVENTION_FILE_MAX_BYTES) {
        setError(`${file.name} exceeds the 1 MB limit.`);
        continue;
      }
      const { type, appliesTo } = classify(file.name);
      next.push({ file, convention_type: type, applies_to: appliesTo });
    }
    const merged = [...entries, ...next];
    setEntries(merged);
    onChange?.(merged.map((e) => e.file));
    if (inputRef.current) inputRef.current.value = '';
  }

  function remove(idx: number): void {
    const merged = entries.filter((_, i) => i !== idx);
    setEntries(merged);
    onChange?.(merged.map((e) => e.file));
  }

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        multiple
        data-testid="conv-file-input"
        style={{ display: 'none' }}
        onChange={(e) => addFiles(e.target.files)}
      />

      {entries.map((e, i) => (
        <div className="file-row" key={`${e.file.name}-${i}`}>
          <span className="file-icon">{e.convention_type.slice(0, 3).toUpperCase()}</span>
          <div>
            <div className="file-name">{e.file.name}</div>
            <div className="file-meta">
              {e.convention_type} · {Math.max(1, Math.round(e.file.size / 1024))} KB
            </div>
          </div>
          <span className="file-applies">applies to · {e.applies_to}</span>
          <button type="button" className="btn ghost sm" onClick={() => remove(i)}>
            Remove
          </button>
        </div>
      ))}

      <div
        className="file-drop"
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          addFiles(e.dataTransfer.files);
        }}
      >
        <span className="file-drop-icon">+</span>
        <div>
          <div className="file-drop-text">Drop a file here</div>
          <div className="file-drop-sub mono">.eslintrc · .prettierrc · stylelint · markdownlint · style-guide.md · xlsx · xls · csv</div>
        </div>
        <button type="button" className="btn ghost sm" onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}>
          Browse
        </button>
      </div>

      {error && (
        <div className="field-err" style={{ marginTop: 12 }} role="alert" data-testid="conv-error">
          {error}
        </div>
      )}
    </div>
  );
}
